import React, { useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Camera, X, RotateCw } from "lucide-react";

const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("user"); // "user" for front, "environment" for back

  useEffect(() => {
    startCamera();
    
    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert("Unable to access camera. Please ensure camera permissions are granted.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      
      // Flip the canvas horizontally to match the mirrored video
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(videoRef.current, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        stopCamera();
      }, 'image/jpeg', 0.95);
    }
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === "user" ? "environment" : "user");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-2 sm:p-4">
      <Card className="w-full max-w-3xl p-3 sm:p-6 bg-white max-h-[95vh] overflow-y-auto">
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <h3 className="text-lg sm:text-xl font-medium text-gray-900">Capture Photo</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors p-1"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
          
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <Button
              onClick={switchCamera}
              variant="outline"
              className="flex items-center gap-2 w-full sm:w-auto text-sm sm:text-base"
              size="sm"
            >
              <RotateCw className="w-4 h-4" />
              <span className="hidden sm:inline">Switch Camera</span>
              <span className="sm:hidden">Switch</span>
            </Button>
            
            <Button
              onClick={capturePhoto}
              className="bg-[#00BCD4] hover:bg-[#00ACC1] text-white px-6 sm:px-8 flex items-center gap-2 w-full sm:w-auto text-sm sm:text-base"
              size="sm"
            >
              <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
              Capture
            </Button>
            
            <Button
              onClick={() => {
                stopCamera();
                onClose();
              }}
              variant="outline"
              className="w-full sm:w-auto text-sm sm:text-base"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CameraCapture;